import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import Cookies from "js-cookie"; // Importing js-cookie for handling cookies
import { io } from "socket.io-client";
import Auth from "../service/AuthService";
import { API_URL } from "helper/Constants";
import { jwtDecode } from "jwt-decode";

// Create the AuthContext
const AuthContext = createContext();

// AuthProvider Component
export const AuthProvider = ({ children }) => {
  const authService = new Auth(API_URL);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const socket = useRef(null);

  const [user, setUser] = useState(() => {
    try {
      const storedUser = Cookies.get("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error("Error parsing stored user:", error);
      return null;
    }
  });

  // Function to get a new access token using the refresh token

  // Function to handle login
  const login = (userData) => {
    try {
      Cookies.set("token", userData.token); // Set the token in cookies
      Cookies.set("user", JSON.stringify(userData)); // Set the user data in cookies
      setUser(userData);
      setIsLoginModalOpen(false);
      connectToSocket(userData.token);
    } catch (error) {
      console.error("Error storing user data during login:", error);
    }
  };

  // Function to update user data
  const updateUser = (updatedData) => {
    setUser((prevUser) => {
      const updatedUser = { ...prevUser, ...updatedData };

      Cookies.set("user", JSON.stringify(updatedUser), {
        secure: true, // Only transmit over HTTPS in production
        sameSite: "None", // Required for cross-origin
      });

      return updatedUser;
    });
  };

  // Function to logout
  const logout = async () => {
    try {
      // await authService.logoutAPICall();
      Cookies.remove("user"); // Remove user from cookies
      Cookies.remove("token"); // Remove token from cookies
      Cookies.remove("refreshToken");
      if (socket.current) {
        socket.current.disconnect();
        socket.current = null;
      } // Remove refresh token from cookies
      setUser(null);
      window.location.href = "/";
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  // Check if the token has expired and refresh it periodically

  // handle login and register button click

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
    setIsRegisterModalOpen(false);
  };
  const handleRegisterClick = () => {
    setIsRegisterModalOpen(true);
    setIsLoginModalOpen(false);
  };
  const handleCloseModal = () => {
    if (isLoginModalOpen) {
      setIsLoginModalOpen(false);
    } else if (isRegisterModalOpen) {
      setIsRegisterModalOpen(false);
    }
  };

  const connectToSocket = useCallback((token) => {
    if (socket.current) return;
    socket.current = io("https://api.3ak3ek.com", {
      auth: { token },
      transports: ["websocket"],
      reconnection: true,
    });

    socket.current.on("connect", () => {
      socket.current.emit("subscribeToBalanceUpdates", { token });
    });

    socket.current.on("balance", (data) => {
      updateUser({ balance: data.balance });
    });

    socket.current.on("balanceUpdate", (data) => {
      updateUser({ balance: data.balance });
    });
    socket.current.on("bonusUpdate", (data) => {
      updateUser({ bonus_balance: data.bonus_balance });
    });
    socket.current.on("forceLogout", (data) => {
      const decoded = jwtDecode(token);
      if (data.userId === decoded.id && decoded.sessionId === data.oldSessionId) {
        logout();
        window.location.reload();
      }
    });
    socket.current.on("disconnect", (reason) => { });
  }, []);

  useEffect(() => {
    if (user && user.token) {
      connectToSocket(user.token);
    }
    return () => {
      if (socket.current) {
        socket.current.disconnect();
        socket.current = null;
      }
    };
  }, [user, connectToSocket]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isRegisterModalOpen,
        isLoginModalOpen,
        login,
        logout,
        updateUser,
        handleLoginClick,
        handleRegisterClick,
        handleCloseModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom Hook for Auth
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
